import type { GameData } from "@/app/games/(details)/types";
import { formatDateOnly } from "@/utils/date";
import { TrailerPlayer } from "@components/NewGamePage/TrailerPlayer/TrailerPlayer";
import Image from "next/image";
import Footer from "../Footer";
import { WindowsLogo } from "../icons/WindowsLogo";
import { ArtGalleryCarousel } from "./ArtGallery/ArtGalleryCarousel";
import { FollowCTA } from "./FollowCTA";
import { GameplayCarousel } from "./Gameplay/GameplayCarousel";
import { GlassButton } from "./GlassButton";
import { RightPanel } from "./RightPanel/RightPanel";
import { VideoIntro } from "./VideoIntro";
import { getPlatformButtons, getSocialButtons } from "./utils";

export const GamePageLayout = ({ gameData }: { gameData: GameData }) => {
  return (
    <VideoIntro
      videoSrc={gameData.gameContent?.video_splash_url}
      accentColour={gameData.gameContent?.accent_colour}
      gameData={gameData}
    >
      {/* Page Container */}
      <div className="flex flex-col flex-1 h-full justify-between px-[4vw] md:px-8 pb-24 md:pb-4 space-y-4">
        <div className="@container relative space-y-5 max-w-[108rem] flex flex-col items-center w-full mx-auto pt-10">
          {/* Header Container */}
          <div className="flex items-center justify-between w-full @3xl:gap-x-10">
            {/* Socials & Release/AMA Container */}
            <div className="space-y-5 flex flex-col items-start flex-1 max-w-[28rem] h-full justify-between relative">
              {/* Socials Glass Buttons */}
              <div className="@3xl:ml-2.5 flex flex-col items-center gap-y-2 @3xl:flex-row @3xl:gap-y-0 @3xl:gap-x-6 justify-start pb-16 @3xl:pb-0">
                {/* TEMP TODO REMOVE @zaidarain1 hardcode for 8-ball, add to retool */}
                {gameData.details.logo_url ? (
                  <GlassButton
                    target="_blank"
                    rel="noopener noreferrer"
                    label=""
                    subLabel=""
                    href={
                      gameData?.gameContent?.store_urls?.find((storeUrl) =>
                        storeUrl?.includes("store.steampowered.com"),
                      ) ?? gameData.details.landing_page_url
                    }
                    className="size-10 md:size-12"
                    labelsClassName="@3xl:block hidden"
                    control="SiteButton"
                    gameId={gameData?.details?.game_id}
                    gameName={gameData?.details?.name}
                  >
                    <Image
                      src={gameData.details.logo_url}
                      alt="Site"
                      width={20}
                      height={20}
                      className="md:scale-100 scale-90"
                    />
                  </GlassButton>
                ) : null}
                {getSocialButtons(
                  gameData.details.social_stats,
                  gameData?.details?.game_id,
                  gameData?.details?.name,
                )}
              </div>
              <div className="@3xl:block hidden w-full h-px bg-white/10" />
              <div className="@3xl:ml-2.5 space-y-0.5 font-suisse absolute bottom-0 left-0 @3xl:static">
                <p className="text-xs leading-none text-neutral-50/60">
                  Release Date
                </p>
                <p className="text-neutral-50 whitespace-nowrap @md:text-base text-sm">
                  {gameData.gameContent?.release_date
                    ? formatDateOnly(
                        new Date(gameData.gameContent.release_date),
                      )
                    : "N/A"}
                </p>
              </div>
            </div>

            {/* Logo */}
            {gameData.details.logo_url ? (
              <Image
                src={gameData.details.logo_url}
                alt={`${gameData.details.name} Logo`}
                width={466}
                height={197}
                className="object-contain max-w-[50%] @2xl:max-w-[40%] @3xl:max-w-[33%]"
              />
            ) : null}

            {/* Platforms & Categories Container */}
            <div className="space-y-5 flex flex-col items-end flex-1 max-w-[28rem] h-full justify-between relative">
              {/* Platforms Glass Buttons */}
              <div className="@3xl:mr-2.5 flex flex-col items-center gap-y-2 @3xl:flex-row @3xl:gap-y-0 @3xl:gap-x-6 justify-end pb-16 @3xl:pb-0">
                {/* TEMP TODO REMOVE @zaidarain1 hardcode if no platforms, show windows */}
                {!gameData.gameContent?.store_urls?.length ? (
                  <GlassButton
                    target="_blank"
                    rel="noopener noreferrer"
                    label="Windows"
                    subLabel=""
                    href=""
                    className="backdrop-blur-0 bg-transparent before:!opacity-0 after:!opacity-0 size-fit"
                    gameId={gameData?.details?.game_id}
                    gameName={gameData?.details?.name}
                    control="WindowsPlatform"
                  >
                    <WindowsLogo className="size-5 scale-150" />
                  </GlassButton>
                ) : (
                  getPlatformButtons(
                    gameData.gameContent.store_urls,
                    gameData?.details?.game_id,
                    gameData?.details?.name,
                  )
                )}
              </div>
              <div className="@3xl:block hidden w-full h-px bg-white/10" />
              <div className="@3xl:mr-2.5 space-y-0.5 font-suisse text-end absolute bottom-0 right-0 @3xl:static">
                <p className="text-xs leading-none text-neutral-50/60">
                  Category
                </p>
                <p className="text-neutral-50 whitespace-nowrap @md:text-base text-sm">
                  {gameData.details.genres.join(", ")}
                </p>
              </div>
            </div>
          </div>

          <FollowCTA gameData={gameData} />

          {/* Bento Cards */}
          <div className="flex flex-col @3xl:flex-row gap-y-2.5 w-full gap-x-2.5">
            <TrailerPlayer
              videoUrl={gameData.gameContent?.trailer_url}
              gameTitle={gameData.details.name}
              gameIcon={gameData.details.logo_url}
              gameId={gameData?.details?.game_id}
              className="w-full @4xl:w-1/2 @3xl:w-2/3"
            />
            <div className="flex gap-x-2.5 w-full flex-row @3xl:w-1/3 @3xl:flex-col gap-y-2.5 @4xl:gap-y-0 @4xl:flex-row @4xl:gap-x-2.5 @4xl:w-1/2">
              <ArtGalleryCarousel
                imageURLs={gameData.gameContent?.art_gallery_urls ?? []}
                gameTitle={gameData.details.name}
                gameIcon={gameData.details.logo_url}
                gameId={gameData?.details?.game_id}
                className="w-full @3xl:h-full aspect-square @3xl:aspect-auto"
              />
              <GameplayCarousel
                mediaURLs={gameData.gameContent?.gameplay_gallery_urls ?? []}
                gameTitle={gameData.details.name}
                gameIcon={gameData.details.logo_url}
                gameId={gameData?.details?.game_id}
                className="w-full @3xl:h-full aspect-square @3xl:aspect-auto"
              />
            </div>
          </div>
        </div>
        <Footer className="!px-0" />
      </div>
      {/* Right Panel - Using flexbox, anchored to right edge */}
      <RightPanel
        className="h-screen sticky top-0 right-0"
        initialNumber={0}
        targetNumber={
          gameData.quests.find((quest) => quest.type === "wishlist")
            ?.participantCount ?? 1000
        }
        gameData={gameData}
        motivationImage={gameData.gameContent?.fan_reward_image_url ?? ""}
        prizeImage={gameData.gameContent?.follow_reward_icon ?? ""}
      />
    </VideoIntro>
  );
};
